import { FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useMeasure } from 'react-use'
import { isDefined } from '@follow/cdk'
import { MedicalEventDocumentType } from '../../../model/MedicalEvent'
import { FullLoader, ValidationModal } from '../../shared'
import WithQuestionContext from '../../../containers/Question/WithQuestionContext'
import { AddMedicalEventContent } from './AddMedicalEventContent'
import { Observations } from './Observations'
import { DocumentInstanceContent } from './DocumentInstanceContent'
import { MedicalEventContentProps } from './MedicalEventContent.model'
import styles from './MedicalEventContent.module.scss'
import { SelectedContentType } from '../../../store/ui/medicalEvents/medicalEventContent'
import { FileInstanceContent } from './FileInstanceContent'
import { FileDropzone, FileViewer } from '../../file'
import classNames from 'classnames'
import { AddEprescriptionTile, OrdoclicIframe } from '../..//eprescription'
import { FileInstanceContentBanner } from './FileInstanceContent/FileInstanceContentBanner'
import {
  extractOwnerOrCreatorNameFromFwDocumentFile,
  generateOwnerCreateNameTitle,
} from '../../../store/domain/medicalEvents/medicalEvents.utils'
import { ConnectedUserContext } from '../../../misc/auth.utilities'
import { useActionDispatch, useCurrentPatient } from '../../../hooks/utils'
import { useCloseOrdoclic, useGetPatientOrdoclicUrl } from '../../../hooks/queries/ordoclic'
import { isAssistant, isDoctor } from '../../../misc/user.utilities'
import { EfficienceUploadConfig } from '../../../model/Efficience'
import { dmpDomainActions, DmpRequestTypes, FileForDMP } from '../../../store/domain/dmp'
import { EfficienceDmpUploadModal } from '../../dmp/EfficienceDmpUploadModal'
import { mapFileForDMP } from '../../../misc/dmp.utilities'
import { useHandlePrescriptionFileGeneration } from '../../../hooks/utils/ordoclic'
import { UploadProgressBarContainer } from './FilesUploadProgressBar/UploadProgressBarContainer'
import { UploadProgressState } from '../../../store/domain/filesUploadProgress/filesUploadProgress.model'

export const MedicalEventContent: FunctionComponent<MedicalEventContentProps> = ({
  medicalEvent,
  documentQuestionList,
  loading,
  questionnaire,
  prescription,
  quoteLine,
  audiometer,
  selectedMedicalEventDocument,
  selectedContentFilter,
  selectMedicalEventContent,
  selectMedicalEventDocument,
  addFilesToMedicalEvent,
  lockDocument,
  updateMedicalEvent,
  userEnabledFeatures,
  filesUploadProgressBar,
}) => {
  const sendPdfsToDMP = useActionDispatch(dmpDomainActions.sendDocumentsToDMP)

  const { currentPatient } = useCurrentPatient()
  const { usurpedUser } = useContext(ConnectedUserContext)
  const [ref, measure] = useMeasure()
  const [openOrdoclic, setOpenOrdoclic] = useState(false)
  const [openEfficienceModal, setOpenEfficienceModal] = useState(false)
  const [selectedOrdoclicPatientIds, setSelectedOrdoclicPatientIds] = useState<string[]>([])
  const { currentUser, loggedUser } = useContext(ConnectedUserContext)

  const [openModalInformation, setOpenModalInformation] = useState(false)

  const { data: ordoclicUrl, refetch } = useGetPatientOrdoclicUrl({
    patientId: currentPatient?.id,
    enabled: false,
  })

  const {
    handlePrescriptionFileGeneration,
    uniquePatientPrescriptionFilesCreated,
    resetPrescriptions,
  } = useHandlePrescriptionFileGeneration(medicalEvent?.id, currentPatient?.id)

  const { mutate: closeOrdoclic } = useCloseOrdoclic()

  const allowedToMakeEPrescription = useMemo(
    () =>
      userEnabledFeatures?.ePrescription &&
      ((isDoctor(currentUser) && !isDefined(usurpedUser)) || isAssistant(loggedUser)),
    [currentUser, loggedUser, userEnabledFeatures?.ePrescription, usurpedUser],
  )

  const creatorAndOwnerNameExtracted = extractOwnerOrCreatorNameFromFwDocumentFile(
    selectedMedicalEventDocument,
    currentUser,
  )

  const ownerAndCreatorName = generateOwnerCreateNameTitle(
    creatorAndOwnerNameExtracted.ownerName,
    creatorAndOwnerNameExtracted.creatorName,
  )

  const closeEfficienceModal = useCallback(() => {
    setOpenEfficienceModal(false)
    resetPrescriptions()
  }, [resetPrescriptions])

  const handleSendPdf = useCallback(
    (config: EfficienceUploadConfig) => {
      if (currentPatient && medicalEvent && uniquePatientPrescriptionFilesCreated) {
        const filesForDmp = uniquePatientPrescriptionFilesCreated.reduce(
          (acc: FileForDMP[], curr) => {
            const fileForDmp = mapFileForDMP(curr)
            if (fileForDmp) acc.push(fileForDmp)
            return acc
          },
          [],
        )

        sendPdfsToDMP(
          currentPatient,
          medicalEvent.owner,
          config,
          DmpRequestTypes.SEND_FILES,
          filesForDmp,
        )
      }

      closeEfficienceModal()
    },
    [
      currentPatient,
      medicalEvent,
      uniquePatientPrescriptionFilesCreated,
      sendPdfsToDMP,
      closeEfficienceModal,
    ],
  )

  const openOrdoclicPrescriptionModal = useCallback(async () => {
    if (allowedToMakeEPrescription) {
      const result = await refetch()
      result.data && setOpenOrdoclic(true)
    } else {
      setOpenModalInformation(true)
    }
  }, [allowedToMakeEPrescription, refetch])

  const closeOrdoclicPrescription = useCallback(() => {
    setOpenOrdoclic(false)
    if (selectedOrdoclicPatientIds.length > 0) {
      closeOrdoclic({ ordoclicPatientIds: selectedOrdoclicPatientIds })
    }

    if (uniquePatientPrescriptionFilesCreated?.length && userEnabledFeatures?.dmpInApp) {
      setOpenEfficienceModal(true)
    }
  }, [
    uniquePatientPrescriptionFilesCreated?.length,
    closeOrdoclic,
    userEnabledFeatures?.dmpInApp,
    selectedOrdoclicPatientIds,
  ])

  const onSelectPatient = useCallback(
    (patientId: string) => {
      if (!selectedOrdoclicPatientIds.includes(patientId)) {
        setSelectedOrdoclicPatientIds((selectedOrdoclicPatientIds) => [
          ...selectedOrdoclicPatientIds,
          patientId,
        ])
      }
    },
    [selectedOrdoclicPatientIds],
  )

  useEffect(() => {
    if (medicalEvent && !medicalEvent.isEditable) {
      selectMedicalEventDocument({
        medicalEventDocumentType: MedicalEventDocumentType.OBSERVATIONS,
      })
    }
  }, [medicalEvent, selectMedicalEventDocument])

  const [shouldDisplayObservation, setShouldDisplayObservation] = useState(false)

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null
    if (selectedMedicalEventDocument?.type === MedicalEventDocumentType.OBSERVATIONS) {
      timeout = setTimeout(() => setShouldDisplayObservation(true), 300)
    } else {
      setShouldDisplayObservation(false)
    }

    return () => (timeout ? clearTimeout(timeout) : undefined)
  }, [selectedMedicalEventDocument])

  if (loading) {
    return <FullLoader />
  }

  if (selectedMedicalEventDocument && medicalEvent) {
    if (selectedMedicalEventDocument.type === MedicalEventDocumentType.FW_DOCUMENT) {
      if (selectedMedicalEventDocument.item.type === 'farte') {
        return (
          <div ref={ref} className={styles.container}>
            <WithQuestionContext
              singleColumnDisplay={measure.width < 1024}
              readOnly={!medicalEvent?.isEditable}
            >
              <DocumentInstanceContent
                key={selectedMedicalEventDocument.item.id}
                medicalEvent={medicalEvent}
                documentInstance={selectedMedicalEventDocument.item}
                documentQuestionList={documentQuestionList}
                questionnaire={questionnaire}
                prescription={prescription}
                quoteLine={quoteLine}
                audiometer={audiometer}
                selectedContent={selectedContentFilter}
                onSelectionChange={() => {
                  selectMedicalEventContent({
                    type: SelectedContentType.ADD_VARIABLE_PANEL,
                  })
                }}
                lockDocument={lockDocument}
              />
            </WithQuestionContext>
          </div>
        )
      } else {
        return (
          <>
            {ownerAndCreatorName && (
              <div className={styles.fileViewerHeaderOwnerCreatorName}>{ownerAndCreatorName}</div>
            )}
            <FileInstanceContent documentInstance={selectedMedicalEventDocument.item} />
          </>
        )
      }
    }

    if (
      selectedMedicalEventDocument.type === MedicalEventDocumentType.OBSERVATIONS &&
      shouldDisplayObservation
    ) {
      return (
        <Observations
          content={medicalEvent.observation ?? ''}
          onChange={(value) => updateMedicalEvent(medicalEvent.id, { observation: value })}
          readonly={!medicalEvent.isEditable}
        />
      )
    }

    if (selectedMedicalEventDocument.type === MedicalEventDocumentType.FILE) {
      const file = selectedMedicalEventDocument.item
      return (
        <>
          <FileInstanceContentBanner
            file={file}
            selectMedicalEventDocument={selectMedicalEventDocument}
          />
          {ownerAndCreatorName && (
            <div className={styles.fileViewerHeaderOwnerCreatorName}>{ownerAndCreatorName}</div>
          )}
          <div className={styles.fileViewer}>
            {file.isOpenable && <FileViewer file={file} enableScrollButton />}
          </div>
        </>
      )
    }
  }

  return medicalEvent ? (
    <div className={styles.addContentContainer}>
      <AddMedicalEventContent medicalEventId={medicalEvent.id} />
      <div className={classNames(styles.floatingTile, styles.dropZone)}>
        <FileDropzone type="file" onDrop={addFilesToMedicalEvent} />
      </div>

      <>
        <div className={classNames(styles.floatingTile, styles.eprescription)}>
          <AddEprescriptionTile onClick={() => openOrdoclicPrescriptionModal()} />
        </div>
        <ValidationModal
          display={openModalInformation}
          icon="warningCircle"
          confirmLabel="Valider"
          title={
            "Rapprochez vous du support (support@follow.fr) ou de votre commercial pour activer VIDAL et/ou fournir votre numéro AM afin de rendre fonctionnel le module d'e-prescription."
          }
          onClose={() => setOpenModalInformation(false)}
        />
        <OrdoclicIframe
          onClose={closeOrdoclicPrescription}
          onSelectPatient={onSelectPatient}
          display={openOrdoclic && !!ordoclicUrl}
          url={ordoclicUrl}
          generatePrescriptionFiles={handlePrescriptionFileGeneration}
          title="Retour à l'événement médical"
        />
        <EfficienceDmpUploadModal
          display={openEfficienceModal}
          onClose={closeEfficienceModal}
          onValid={handleSendPdf}
        />
      </>

      {filesUploadProgressBar && (
        <UploadProgressBarContainer {...(filesUploadProgressBar as UploadProgressState)} />
      )}
    </div>
  ) : null
}
